/**
 * SkinsShopRenderer — per-tank visual customization screen.
 *
 * Two-column layout: Body+Nub on left, Color+Border on right.
 * Centered preview tank with title sections below.
 * Opaque background — no game world bleeds through.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { WORLD, TANK_VISUAL } from '../../constants';
import { getAllTankIds, lookupTank } from '../../tanks';
import { isColorUnlocked, purchaseColor, getCoins } from '../../progression/storage';
import { COLOR_PRICE } from '../../progression/catalog';
import { drawTankShape, strokeTankShape, getTankShapeDef, lighten } from '../shapes';
import { drawBarrel } from '../nub-tips';
import type { GameState } from '../../types';
import {
  getSkinForTank, setSkinProperty, resetSkin,
  SKIN_COLORS, BODY_SHAPE_IDS, NUB_TYPE_IDS, BORDER_STYLES,
  TITLES, TITLE_COLORS,
  NUB_LABELS,
  type TankSkinOverride,
} from '../../skins/storage';

// ─── Styling ──────────────────────────────────────────────────────────────────

function monoStyle(size: number, color: number, bold = false): TextStyle {
  return new TextStyle({
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: size,
    fill: color,
    fontWeight: bold ? 'bold' : 'normal',
  });
}

// ─── Layout Constants ─────────────────────────────────────────────────────────

const CX = 960; // center x for 1920 canvas

const LAYOUT = {
  // Background
  BG_COLOR: 0x08080f,

  // Title
  TITLE_Y: 30,

  // Tank row
  TANK_ROW_Y: 75,
  TANK_CELL_SIZE: 48,
  TANK_CELL_GAP: 6,

  // Left column (Body + Nub)
  LEFT_X: 80,
  LEFT_Y: 140,

  // Right column (Color + Border)
  RIGHT_X: 1100,
  RIGHT_Y: 140,

  // Preview
  PREVIEW_X: CX,
  PREVIEW_Y: 400,
  PREVIEW_RADIUS: 80,

  // Tank name below preview
  TANK_NAME_Y: 500,

  // Title preview above preview
  TITLE_PREVIEW_Y: 300,

  // Title cycle selector (centered)
  TITLE_SELECTOR_Y: 560,
  TITLE_SELECTOR_WIDTH: 300,

  // Title color grid (centered)
  TITLE_COLOR_Y: 620,
  TITLE_COLOR_COLS: 9,

  // Section grids
  CELL_SIZE: 36,
  CELL_GAP: 4,
  COLS_BODY: 9,
  COLS_NUB: 13,
  COLS_COLOR: 9,
  COLS_BORDER: 6,
  SECTION_GAP: 10,
  HEADER_SIZE: 14,

  // Bottom bar
  BOTTOM_Y: 1020,

  CORNER_RADIUS: 4,
  GLOW_PULSE_SPEED: 4,
} as const;

const STEP = LAYOUT.CELL_SIZE + LAYOUT.CELL_GAP; // 40px per cell

// Section definitions: 0=Body, 1=Nub, 2=Color, 3=Border, 4=Title, 5=TitleColor, 6=Reset
const SECTION_NAMES = ['BODY SHAPE', 'TURRET NUB', 'COLOR', 'BORDER STYLE', 'TITLE', 'TITLE COLOR', ''];
const SECTION_ITEM_COUNTS = [
  BODY_SHAPE_IDS.length,
  NUB_TYPE_IDS.length,
  SKIN_COLORS.length,
  BORDER_STYLES.length,
  TITLES.length,
  TITLE_COLORS.length,
  1,
];
const SECTION_COLS = [
  LAYOUT.COLS_BODY,
  LAYOUT.COLS_NUB,
  LAYOUT.COLS_COLOR,
  LAYOUT.COLS_BORDER,
  1,  // Title: cycle selector
  LAYOUT.TITLE_COLOR_COLS,
  1,
];

// ─── Renderer ─────────────────────────────────────────────────────────────────

export class SkinsShopRenderer {
  private container: Container;
  private elapsed = 0;

  // Tank row
  private tankRowCells: Graphics[] = [];
  private tankRowLabels: Text[] = [];

  // Preview
  private previewContainer: Container;
  private previewBodyGfx: Graphics;
  private previewTurretGfx: Graphics;
  private previewBorderGfx: Graphics;
  private previewAngle = 0;

  // Options
  private sectionLabels: Text[] = [];
  private optionCells: Graphics[][] = []; // [section][item]
  private optionMiniGfx: Graphics[][] = []; // mini shape/nub/color previews inside cells
  private sectionCursor: Graphics;

  // Reset + Back
  private resetBtn: Graphics;
  private resetLabel: Text;
  private backLabel: Text;

  // Tank name
  private tankNameText: Text;
  // Hover label
  private hoverLabel: Text;
  // Title cycle selector (section 4)
  private titleCycleLabel: Text;
  private titleCycleBox: Graphics;
  // Title preview above tank
  private previewTitleText: Text;

  constructor(
    container: Container,
    private tapVirtualKey: (code: string) => void,
    private getState: () => GameState | null,
  ) {
    this.container = container;

    // ─── Opaque Background ────────────────────────────
    const bg = new Graphics();
    bg.rect(0, 0, WORLD.CANVAS_WIDTH, WORLD.CANVAS_HEIGHT);
    bg.fill({ color: LAYOUT.BG_COLOR, alpha: 1.0 });
    bg.eventMode = 'static';
    container.addChild(bg);

    // ─── Title ────────────────────────────────────────
    const title = new Text({ text: 'SKINS SHOP', style: monoStyle(26, 0xffffff, true) });
    title.anchor.set(0.5, 0);
    title.position.set(CX, LAYOUT.TITLE_Y);
    container.addChild(title);

    // ─── Tank Row ─────────────────────────────────────
    const tankIds = getAllTankIds();
    const tankRowWidth = tankIds.length * LAYOUT.TANK_CELL_SIZE + (tankIds.length - 1) * LAYOUT.TANK_CELL_GAP;
    const tankRowStartX = CX - tankRowWidth / 2;

    for (let i = 0; i < tankIds.length; i++) {
      const x = tankRowStartX + i * (LAYOUT.TANK_CELL_SIZE + LAYOUT.TANK_CELL_GAP);
      const cellGfx = new Graphics();
      cellGfx.position.set(x, LAYOUT.TANK_ROW_Y);
      container.addChild(cellGfx);
      this.tankRowCells.push(cellGfx);

      // Touch target
      const hitRect = new Graphics();
      hitRect.rect(0, 0, LAYOUT.TANK_CELL_SIZE, LAYOUT.TANK_CELL_SIZE);
      hitRect.fill({ color: 0x000000, alpha: 0.001 });
      hitRect.position.set(x, LAYOUT.TANK_ROW_Y);
      hitRect.eventMode = 'static';
      hitRect.cursor = 'pointer';
      const idx = i;
      hitRect.on('pointerdown', () => {
        const s = this.getState();
        if (s) { s.skinsShopTank = idx; s.skinsShopSection = 0; s.skinsShopItem = 0; }
      });
      container.addChild(hitRect);

      // Label below cell
      const td = lookupTank(tankIds[i]);
      const label = new Text({ text: td.name.substring(0, 4).toUpperCase(), style: monoStyle(8, 0x666666) });
      label.anchor.set(0.5, 0);
      label.position.set(x + LAYOUT.TANK_CELL_SIZE / 2, LAYOUT.TANK_ROW_Y + LAYOUT.TANK_CELL_SIZE + 1);
      container.addChild(label);
      this.tankRowLabels.push(label);
    }

    // ─── Left Column: Body + Nub ──────────────────────
    let leftY: number = LAYOUT.LEFT_Y;
    leftY = this.buildGridSection(container, 0, LAYOUT.LEFT_X, leftY);
    leftY = this.buildGridSection(container, 1, LAYOUT.LEFT_X, leftY);

    // ─── Right Column: Color + Border ─────────────────
    let rightY: number = LAYOUT.RIGHT_Y;
    rightY = this.buildGridSection(container, 2, LAYOUT.RIGHT_X, rightY);
    rightY = this.buildGridSection(container, 3, LAYOUT.RIGHT_X, rightY);

    // ─── Preview ──────────────────────────────────────
    this.previewContainer = new Container();
    this.previewContainer.position.set(LAYOUT.PREVIEW_X, LAYOUT.PREVIEW_Y);
    container.addChild(this.previewContainer);

    this.previewBodyGfx = new Graphics();
    this.previewContainer.addChild(this.previewBodyGfx);

    this.previewBorderGfx = new Graphics();
    this.previewContainer.addChild(this.previewBorderGfx);

    this.previewTurretGfx = new Graphics();
    this.previewContainer.addChild(this.previewTurretGfx);

    // Preview title text (above tank)
    this.previewTitleText = new Text({ text: '', style: monoStyle(16, 0xffffff, true) });
    this.previewTitleText.anchor.set(0.5, 1);
    this.previewTitleText.position.set(0, -(LAYOUT.PREVIEW_RADIUS + 16));
    this.previewTitleText.visible = false;
    this.previewContainer.addChild(this.previewTitleText);

    // Tank name below preview
    this.tankNameText = new Text({ text: '', style: monoStyle(16, 0x888888, true) });
    this.tankNameText.anchor.set(0.5, 0);
    this.tankNameText.position.set(CX, LAYOUT.TANK_NAME_Y);
    container.addChild(this.tankNameText);

    // ─── Section 4: Title (cycle selector, centered) ──
    {
      const secLabel = new Text({ text: SECTION_NAMES[4], style: monoStyle(LAYOUT.HEADER_SIZE, 0x888888, true) });
      secLabel.anchor.set(0.5, 0);
      secLabel.position.set(CX, LAYOUT.TITLE_SELECTOR_Y - 20);
      container.addChild(secLabel);
      this.sectionLabels.push(secLabel);

      const boxX = CX - LAYOUT.TITLE_SELECTOR_WIDTH / 2;
      this.titleCycleBox = new Graphics();
      this.titleCycleBox.position.set(boxX, LAYOUT.TITLE_SELECTOR_Y);
      container.addChild(this.titleCycleBox);

      this.titleCycleLabel = new Text({ text: 'None', style: monoStyle(14, 0xcccccc, true) });
      this.titleCycleLabel.anchor.set(0.5, 0.5);
      this.titleCycleLabel.position.set(CX, LAYOUT.TITLE_SELECTOR_Y + 18);
      container.addChild(this.titleCycleLabel);

      // Touch targets for arrows
      const arrowLeft = new Graphics();
      arrowLeft.rect(0, 0, 40, 36);
      arrowLeft.fill({ color: 0x000000, alpha: 0.001 });
      arrowLeft.position.set(boxX, LAYOUT.TITLE_SELECTOR_Y);
      arrowLeft.eventMode = 'static';
      arrowLeft.cursor = 'pointer';
      arrowLeft.on('pointerdown', () => {
        const s = this.getState();
        if (s) {
          s.skinsShopSection = 4;
          s.skinsShopItem = (s.skinsShopItem - 1 + TITLES.length) % TITLES.length;
          this.applySelection(s);
        }
      });
      container.addChild(arrowLeft);

      const arrowRight = new Graphics();
      arrowRight.rect(0, 0, 40, 36);
      arrowRight.fill({ color: 0x000000, alpha: 0.001 });
      arrowRight.position.set(boxX + LAYOUT.TITLE_SELECTOR_WIDTH - 40, LAYOUT.TITLE_SELECTOR_Y);
      arrowRight.eventMode = 'static';
      arrowRight.cursor = 'pointer';
      arrowRight.on('pointerdown', () => {
        const s = this.getState();
        if (s) {
          s.skinsShopSection = 4;
          s.skinsShopItem = (s.skinsShopItem + 1) % TITLES.length;
          this.applySelection(s);
        }
      });
      container.addChild(arrowRight);

      // Empty arrays for indexing consistency
      this.optionCells.push([]);
      this.optionMiniGfx.push([]);
    }

    // ─── Section 5: Title Color (grid, centered) ──────
    {
      const secLabel = new Text({ text: SECTION_NAMES[5], style: monoStyle(LAYOUT.HEADER_SIZE, 0x888888, true) });
      secLabel.anchor.set(0.5, 0);
      secLabel.position.set(CX, LAYOUT.TITLE_COLOR_Y - 20);
      container.addChild(secLabel);
      this.sectionLabels.push(secLabel);

      const count = TITLE_COLORS.length;
      const cols = LAYOUT.TITLE_COLOR_COLS;
      const gridWidth = cols * STEP - LAYOUT.CELL_GAP;
      const gridStartX = CX - gridWidth / 2;
      const cells: Graphics[] = [];
      const minis: Graphics[] = [];

      for (let j = 0; j < count; j++) {
        const col = j % cols;
        const row = Math.floor(j / cols);
        const cellX = gridStartX + col * STEP;
        const cellY = LAYOUT.TITLE_COLOR_Y + row * STEP;

        const cellGfx = new Graphics();
        cellGfx.position.set(cellX, cellY);
        container.addChild(cellGfx);
        cells.push(cellGfx);

        const miniGfx = new Graphics();
        miniGfx.position.set(cellX + LAYOUT.CELL_SIZE / 2, cellY + LAYOUT.CELL_SIZE / 2);
        container.addChild(miniGfx);
        minis.push(miniGfx);

        // Touch target
        const hitR = new Graphics();
        hitR.rect(0, 0, LAYOUT.CELL_SIZE, LAYOUT.CELL_SIZE);
        hitR.fill({ color: 0x000000, alpha: 0.001 });
        hitR.position.set(cellX, cellY);
        hitR.eventMode = 'static';
        hitR.cursor = 'pointer';
        const item = j;
        hitR.on('pointerdown', () => {
          const s = this.getState();
          if (s) {
            s.skinsShopSection = 5;
            s.skinsShopItem = item;
            this.applySelection(s);
          }
        });
        container.addChild(hitR);
      }

      this.optionCells.push(cells);
      this.optionMiniGfx.push(minis);
    }

    // Section cursor highlight
    this.sectionCursor = new Graphics();
    container.addChild(this.sectionCursor);

    // ─── Bottom Bar ───────────────────────────────────
    // Hover label (centered-ish)
    this.hoverLabel = new Text({ text: '', style: monoStyle(12, 0x999999) });
    this.hoverLabel.anchor.set(0.5, 0.5);
    this.hoverLabel.position.set(CX, LAYOUT.BOTTOM_Y);
    container.addChild(this.hoverLabel);

    // Back / switch tank hints (left)
    this.backLabel = new Text({ text: 'ESC  Back       Q/E  Switch Tank', style: monoStyle(12, 0x444444) });
    this.backLabel.anchor.set(0, 0.5);
    this.backLabel.position.set(40, LAYOUT.BOTTOM_Y);
    container.addChild(this.backLabel);

    // Reset button (right)
    this.resetBtn = new Graphics();
    this.resetBtn.position.set(WORLD.CANVAS_WIDTH - 240, LAYOUT.BOTTOM_Y - 18);
    container.addChild(this.resetBtn);

    this.resetLabel = new Text({ text: 'RESET TO DEFAULT', style: monoStyle(13, 0xaaaaaa, true) });
    this.resetLabel.anchor.set(0, 0.5);
    this.resetLabel.position.set(WORLD.CANVAS_WIDTH - 226, LAYOUT.BOTTOM_Y);
    container.addChild(this.resetLabel);

    // Reset touch target
    const resetHit = new Graphics();
    resetHit.rect(0, 0, 200, 36);
    resetHit.fill({ color: 0x000000, alpha: 0.001 });
    resetHit.position.set(WORLD.CANVAS_WIDTH - 240, LAYOUT.BOTTOM_Y - 18);
    resetHit.eventMode = 'static';
    resetHit.cursor = 'pointer';
    resetHit.on('pointerdown', () => {
      const s = this.getState();
      if (s) {
        s.skinsShopSection = 6;
        s.skinsShopItem = 0;
        this.applySelection(s);
      }
    });
    container.addChild(resetHit);
  }

  /** Build a standard grid section (body/nub/color/border). Returns the Y after the section. */
  private buildGridSection(container: Container, sec: number, startX: number, startY: number): number {
    // Header
    const secLabel = new Text({ text: SECTION_NAMES[sec], style: monoStyle(LAYOUT.HEADER_SIZE, 0x888888, true) });
    secLabel.position.set(startX, startY);
    container.addChild(secLabel);
    this.sectionLabels.push(secLabel);

    const gridY = startY + 22;
    const count = SECTION_ITEM_COUNTS[sec];
    const cols = SECTION_COLS[sec];
    const cells: Graphics[] = [];
    const minis: Graphics[] = [];

    for (let j = 0; j < count; j++) {
      const col = j % cols;
      const row = Math.floor(j / cols);
      const cellX = startX + col * STEP;
      const cellY = gridY + row * STEP;

      const cellGfx = new Graphics();
      cellGfx.position.set(cellX, cellY);
      container.addChild(cellGfx);
      cells.push(cellGfx);

      const miniGfx = new Graphics();
      miniGfx.position.set(cellX + LAYOUT.CELL_SIZE / 2, cellY + LAYOUT.CELL_SIZE / 2);
      container.addChild(miniGfx);
      minis.push(miniGfx);

      // Touch target
      const hitR = new Graphics();
      hitR.rect(0, 0, LAYOUT.CELL_SIZE, LAYOUT.CELL_SIZE);
      hitR.fill({ color: 0x000000, alpha: 0.001 });
      hitR.position.set(cellX, cellY);
      hitR.eventMode = 'static';
      hitR.cursor = 'pointer';
      const section = sec, item = j;
      hitR.on('pointerdown', () => {
        const s = this.getState();
        if (s) {
          s.skinsShopSection = section;
          s.skinsShopItem = item;
          this.applySelection(s);
        }
      });
      container.addChild(hitR);
    }

    this.optionCells.push(cells);
    this.optionMiniGfx.push(minis);

    const rows = Math.ceil(count / cols);
    return gridY + rows * STEP + LAYOUT.SECTION_GAP;
  }

  update(state: GameState, elapsed: number): void {
    this.elapsed = elapsed;
    const tankIds = getAllTankIds();
    const selectedTankId = tankIds[state.skinsShopTank] ?? 'mortar';
    const skin = getSkinForTank(selectedTankId);
    const tankDef = lookupTank(selectedTankId);

    // Resolve current visuals
    const bodyId = skin.bodyShape ?? selectedTankId;
    const nubId = skin.nubType ?? selectedTankId;
    const color = skin.color ?? tankDef.visual.bodyColor;
    const borderId = skin.borderStyle ?? 'none';

    // ─── Tank Row ─────────────────────────────────────
    for (let i = 0; i < tankIds.length; i++) {
      const cellGfx = this.tankRowCells[i];
      if (!cellGfx) continue;
      const isSelected = i === state.skinsShopTank;
      const td = lookupTank(tankIds[i]);
      const tdColor = getSkinForTank(tankIds[i]).color ?? td.visual.bodyColor;

      cellGfx.clear();
      cellGfx.roundRect(0, 0, LAYOUT.TANK_CELL_SIZE, LAYOUT.TANK_CELL_SIZE, 4);
      cellGfx.fill({ color: 0x0a0a15, alpha: isSelected ? 0.7 : 0.3 });
      cellGfx.roundRect(0, 0, LAYOUT.TANK_CELL_SIZE, LAYOUT.TANK_CELL_SIZE, 4);

      if (isSelected) {
        const pulse = 0.7 + Math.sin(this.elapsed * LAYOUT.GLOW_PULSE_SPEED) * 0.3;
        cellGfx.stroke({ color: tdColor, width: 2, alpha: pulse });
      } else {
        cellGfx.stroke({ color: tdColor, width: 1, alpha: 0.3 });
      }

      // Center dot
      const cx = LAYOUT.TANK_CELL_SIZE / 2;
      const cy = LAYOUT.TANK_CELL_SIZE / 2;
      cellGfx.circle(cx, cy, 3);
      cellGfx.fill({ color: tdColor, alpha: 0.6 });

      this.tankRowLabels[i].style.fill = isSelected ? 0xffffff : 0x666666;
    }

    // ─── Preview ──────────────────────────────────────
    this.previewAngle += 0.008;

    this.previewBodyGfx.clear();
    drawTankShape(this.previewBodyGfx, bodyId, LAYOUT.PREVIEW_RADIUS, color);

    this.previewTurretGfx.clear();
    this.previewTurretGfx.rotation = this.previewAngle;
    drawBarrel(this.previewTurretGfx, nubId, color);

    this.previewBorderGfx.clear();
    if (borderId !== 'none') {
      this.drawPreviewBorder(borderId, bodyId, LAYOUT.PREVIEW_RADIUS, elapsed);
    }

    // Tank name
    this.tankNameText.text = tankDef.name.toUpperCase();

    // Hover label
    this.updateHoverLabel(state, bodyId, nubId, color, borderId);

    // ─── Section 0: Body shapes ───────────────────────
    for (let j = 0; j < BODY_SHAPE_IDS.length; j++) {
      const cellGfx = this.optionCells[0]?.[j];
      const miniGfx = this.optionMiniGfx[0]?.[j];
      if (!cellGfx || !miniGfx) continue;

      const shapeId = BODY_SHAPE_IDS[j];
      const isActive = bodyId === shapeId;
      const isHighlighted = state.skinsShopSection === 0 && state.skinsShopItem === j;

      this.drawOptionCell(cellGfx, isActive, isHighlighted, color, elapsed);

      miniGfx.clear();
      drawTankShape(miniGfx, shapeId, 10, isActive ? color : 0x555555);
    }

    // ─── Section 1: Nub types ─────────────────────────
    for (let j = 0; j < NUB_TYPE_IDS.length; j++) {
      const cellGfx = this.optionCells[1]?.[j];
      const miniGfx = this.optionMiniGfx[1]?.[j];
      if (!cellGfx || !miniGfx) continue;

      const nubTankId = NUB_TYPE_IDS[j];
      const isActive = nubId === nubTankId;
      const isHighlighted = state.skinsShopSection === 1 && state.skinsShopItem === j;

      this.drawOptionCell(cellGfx, isActive, isHighlighted, color, elapsed);

      miniGfx.clear();
      miniGfx.scale.set(0.4);
      drawBarrel(miniGfx, nubTankId, isActive ? color : 0x555555);
    }

    // ─── Section 2: Colors ────────────────────────────
    // Build default tank colors set for free-color check
    const defaultTankColors = getAllTankIds().map(id => lookupTank(id).visual.bodyColor);

    for (let j = 0; j < SKIN_COLORS.length; j++) {
      const cellGfx = this.optionCells[2]?.[j];
      const miniGfx = this.optionMiniGfx[2]?.[j];
      if (!cellGfx || !miniGfx) continue;

      const swatchColor = SKIN_COLORS[j].color;
      const isActive = color === swatchColor;
      const isHighlighted = state.skinsShopSection === 2 && state.skinsShopItem === j;
      const locked = !isColorUnlocked(swatchColor, defaultTankColors);

      cellGfx.clear();
      cellGfx.roundRect(0, 0, LAYOUT.CELL_SIZE, LAYOUT.CELL_SIZE, LAYOUT.CORNER_RADIUS);
      cellGfx.fill({ color: 0x0a0a15, alpha: isHighlighted ? 0.7 : 0.3 });
      cellGfx.roundRect(0, 0, LAYOUT.CELL_SIZE, LAYOUT.CELL_SIZE, LAYOUT.CORNER_RADIUS);

      if (isActive) {
        cellGfx.stroke({ color: swatchColor, width: 2.5, alpha: 1.0 });
      } else if (isHighlighted) {
        const pulse = 0.6 + Math.sin(this.elapsed * LAYOUT.GLOW_PULSE_SPEED) * 0.4;
        cellGfx.stroke({ color: 0xffffff, width: 1.5, alpha: pulse });
      } else {
        cellGfx.stroke({ color: locked ? 0x555533 : 0x333333, width: 1, alpha: 0.3 });
      }

      miniGfx.clear();
      if (locked) {
        // Locked — dim swatch with lock indicator
        miniGfx.circle(0, 0, 10);
        miniGfx.fill({ color: swatchColor, alpha: 0.25 });
        miniGfx.circle(0, 0, 3);
        miniGfx.fill({ color: 0xffd700, alpha: 0.8 });
      } else {
        miniGfx.circle(0, 0, 10);
        miniGfx.fill({ color: swatchColor, alpha: isActive ? 1.0 : 0.6 });
        if (isActive) {
          miniGfx.circle(0, 0, 10);
          miniGfx.stroke({ color: 0xffffff, width: 1.5, alpha: 0.6 });
        }
      }
    }

    // ─── Section 3: Border styles ─────────────────────
    for (let j = 0; j < BORDER_STYLES.length; j++) {
      const cellGfx = this.optionCells[3]?.[j];
      const miniGfx = this.optionMiniGfx[3]?.[j];
      if (!cellGfx || !miniGfx) continue;

      const bStyle = BORDER_STYLES[j];
      const isActive = borderId === bStyle.id;
      const isHighlighted = state.skinsShopSection === 3 && state.skinsShopItem === j;

      cellGfx.clear();
      cellGfx.roundRect(0, 0, LAYOUT.CELL_SIZE, LAYOUT.CELL_SIZE, LAYOUT.CORNER_RADIUS);
      cellGfx.fill({ color: 0x0a0a15, alpha: isHighlighted ? 0.7 : 0.3 });
      cellGfx.roundRect(0, 0, LAYOUT.CELL_SIZE, LAYOUT.CELL_SIZE, LAYOUT.CORNER_RADIUS);

      if (isActive) {
        cellGfx.stroke({ color: bStyle.color, width: 2.5, alpha: 1.0 });
      } else if (isHighlighted) {
        const pulse = 0.6 + Math.sin(this.elapsed * LAYOUT.GLOW_PULSE_SPEED) * 0.4;
        cellGfx.stroke({ color: 0xffffff, width: 1.5, alpha: pulse });
      } else {
        cellGfx.stroke({ color: 0x333333, width: 1, alpha: 0.3 });
      }

      miniGfx.clear();
      if (bStyle.id === 'none') {
        miniGfx.moveTo(-5, -5); miniGfx.lineTo(5, 5);
        miniGfx.moveTo(5, -5); miniGfx.lineTo(-5, 5);
        miniGfx.stroke({ color: 0x444444, width: 1.5, alpha: 0.5 });
      } else if (bStyle.id === 'rainbow') {
        for (let layer = 0; layer < 3; layer++) {
          const hue = (layer * 120 + this.elapsed * 80) % 360;
          miniGfx.circle(0, 0, 8 + layer * 0.3);
          miniGfx.stroke({ color: hueToHex(hue), width: 1.5, alpha: isActive ? 0.7 : 0.4 });
        }
      } else {
        miniGfx.circle(0, 0, 8);
        miniGfx.stroke({ color: bStyle.color, width: 2, alpha: isActive ? 0.9 : 0.5 });
      }
    }

    // ─── Section 4: Title cycle selector ──────────────
    const titleId = skin.title ?? 'none';
    const titleRainbow = skin.titleRainbow ?? false;
    const titleColorVal = skin.titleColor ?? color;
    {
      const isHighlighted = state.skinsShopSection === 4;
      const titleEntry = TITLES.find(t => t.id === titleId);
      const displayItem = state.skinsShopSection === 4 ? TITLES[state.skinsShopItem] : titleEntry;
      this.titleCycleLabel.text = displayItem?.label ?? 'None';
      this.titleCycleLabel.style.fill = isHighlighted ? 0xffffff : 0x999999;

      const w = LAYOUT.TITLE_SELECTOR_WIDTH;
      this.titleCycleBox.clear();
      this.titleCycleBox.roundRect(0, 0, w, 36, 4);
      this.titleCycleBox.fill({ color: 0x0a0a15, alpha: isHighlighted ? 0.7 : 0.3 });
      this.titleCycleBox.roundRect(0, 0, w, 36, 4);
      if (isHighlighted) {
        const pulse = 0.6 + Math.sin(this.elapsed * LAYOUT.GLOW_PULSE_SPEED) * 0.4;
        this.titleCycleBox.stroke({ color: color, width: 1.5, alpha: pulse });
      } else {
        this.titleCycleBox.stroke({ color: 0x333333, width: 1, alpha: 0.3 });
      }

      // Arrows
      const arrowColor = isHighlighted ? 0xffffff : 0x555555;
      // Left arrow
      this.titleCycleBox.moveTo(15, 18);
      this.titleCycleBox.lineTo(8, 18);
      this.titleCycleBox.moveTo(12, 14);
      this.titleCycleBox.lineTo(8, 18);
      this.titleCycleBox.lineTo(12, 22);
      this.titleCycleBox.stroke({ color: arrowColor, width: 1.5, alpha: 0.8 });
      // Right arrow
      this.titleCycleBox.moveTo(w - 15, 18);
      this.titleCycleBox.lineTo(w - 8, 18);
      this.titleCycleBox.moveTo(w - 12, 14);
      this.titleCycleBox.lineTo(w - 8, 18);
      this.titleCycleBox.lineTo(w - 12, 22);
      this.titleCycleBox.stroke({ color: arrowColor, width: 1.5, alpha: 0.8 });
    }

    // ─── Section 5: Title Color grid ──────────────────
    for (let j = 0; j < TITLE_COLORS.length; j++) {
      const cellGfx = this.optionCells[5]?.[j];
      const miniGfx = this.optionMiniGfx[5]?.[j];
      if (!cellGfx || !miniGfx) continue;

      const tc = TITLE_COLORS[j];
      const isActive = tc.rainbow ? titleRainbow : (!titleRainbow && titleColorVal === tc.color);
      const isHighlighted = state.skinsShopSection === 5 && state.skinsShopItem === j;

      cellGfx.clear();
      cellGfx.roundRect(0, 0, LAYOUT.CELL_SIZE, LAYOUT.CELL_SIZE, LAYOUT.CORNER_RADIUS);
      cellGfx.fill({ color: 0x0a0a15, alpha: isHighlighted ? 0.7 : 0.3 });
      cellGfx.roundRect(0, 0, LAYOUT.CELL_SIZE, LAYOUT.CELL_SIZE, LAYOUT.CORNER_RADIUS);

      if (isActive) {
        cellGfx.stroke({ color: tc.rainbow ? hueToHex((this.elapsed * 120) % 360) : tc.color, width: 2.5, alpha: 1.0 });
      } else if (isHighlighted) {
        const pulse = 0.6 + Math.sin(this.elapsed * LAYOUT.GLOW_PULSE_SPEED) * 0.4;
        cellGfx.stroke({ color: 0xffffff, width: 1.5, alpha: pulse });
      } else {
        cellGfx.stroke({ color: 0x333333, width: 1, alpha: 0.3 });
      }

      miniGfx.clear();
      if (tc.rainbow) {
        for (let layer = 0; layer < 3; layer++) {
          const hue = (layer * 120 + this.elapsed * 80) % 360;
          miniGfx.circle(0, 0, 8 + layer * 0.3);
          miniGfx.stroke({ color: hueToHex(hue), width: 1.5, alpha: isActive ? 0.7 : 0.4 });
        }
      } else {
        miniGfx.circle(0, 0, 10);
        miniGfx.fill({ color: tc.color, alpha: isActive ? 1.0 : 0.6 });
        if (isActive) {
          miniGfx.circle(0, 0, 10);
          miniGfx.stroke({ color: 0xffffff, width: 1.5, alpha: 0.6 });
        }
      }
    }

    // ─── Preview Title Text ───────────────────────────
    {
      const previewTitleId = state.skinsShopSection === 4 ? TITLES[state.skinsShopItem]?.id : titleId;
      const titleEntry = TITLES.find(t => t.id === previewTitleId);
      const hasTitle = previewTitleId && previewTitleId !== 'none' && titleEntry;
      this.previewTitleText.visible = !!hasTitle;
      if (hasTitle) {
        this.previewTitleText.text = titleEntry.label;
        if (titleRainbow) {
          this.previewTitleText.style.fill = hueToHex((this.elapsed * 120) % 360);
        } else {
          this.previewTitleText.style.fill = titleColorVal;
        }
      }
    }

    // ─── Reset Button ─────────────────────────────────
    const isResetHighlighted = state.skinsShopSection === 6;
    const hasSkin = skin.bodyShape !== undefined || skin.nubType !== undefined || skin.color !== undefined || skin.borderStyle !== undefined || (skin.title !== undefined && skin.title !== 'none');
    this.resetBtn.clear();
    this.resetBtn.roundRect(0, 0, 200, 36, 4);
    this.resetBtn.fill({ color: 0x0a0a15, alpha: 0.5 });
    this.resetBtn.roundRect(0, 0, 200, 36, 4);
    if (isResetHighlighted) {
      const pulse = 0.6 + Math.sin(this.elapsed * LAYOUT.GLOW_PULSE_SPEED) * 0.4;
      this.resetBtn.stroke({ color: 0xff4444, width: 2, alpha: pulse });
    } else {
      this.resetBtn.stroke({ color: 0x444444, width: 1, alpha: 0.5 });
    }
    this.resetLabel.style.fill = hasSkin ? (isResetHighlighted ? 0xff6666 : 0xaaaaaa) : 0x444444;
  }

  /** Apply the currently highlighted selection to the skin store. */
  applySelection(state: GameState): void {
    const tankIds = getAllTankIds();
    const selectedTankId = tankIds[state.skinsShopTank] ?? 'mortar';

    if (state.skinsShopSection === 0) {
      const shapeId = BODY_SHAPE_IDS[state.skinsShopItem];
      if (shapeId) {
        if (shapeId === selectedTankId) {
          removeSkinProp(selectedTankId, 'bodyShape');
        } else {
          setSkinProperty(selectedTankId, 'bodyShape', shapeId);
        }
      }
    } else if (state.skinsShopSection === 1) {
      const nId = NUB_TYPE_IDS[state.skinsShopItem];
      if (nId) {
        if (nId === selectedTankId) {
          removeSkinProp(selectedTankId, 'nubType');
        } else {
          setSkinProperty(selectedTankId, 'nubType', nId);
        }
      }
    } else if (state.skinsShopSection === 2) {
      const c = SKIN_COLORS[state.skinsShopItem];
      if (c) {
        // Check if color is locked (not a default tank color and not purchased)
        const defColors = getAllTankIds().map(id => lookupTank(id).visual.bodyColor);
        if (!isColorUnlocked(c.color, defColors)) {
          // Attempt purchase
          purchaseColor(c.color);
          return;
        }
        const defaultColor = lookupTank(selectedTankId).visual.bodyColor;
        if (c.color === defaultColor) {
          removeSkinProp(selectedTankId, 'color');
        } else {
          setSkinProperty(selectedTankId, 'color', c.color);
        }
      }
    } else if (state.skinsShopSection === 3) {
      const b = BORDER_STYLES[state.skinsShopItem];
      if (b) {
        if (b.id === 'none') {
          removeSkinProp(selectedTankId, 'borderStyle');
        } else {
          setSkinProperty(selectedTankId, 'borderStyle', b.id);
        }
      }
    } else if (state.skinsShopSection === 4) {
      const t = TITLES[state.skinsShopItem];
      if (t) {
        if (t.id === 'none') {
          removeSkinProp(selectedTankId, 'title');
        } else {
          setSkinProperty(selectedTankId, 'title', t.id);
        }
      }
    } else if (state.skinsShopSection === 5) {
      const tc = TITLE_COLORS[state.skinsShopItem];
      if (tc) {
        if (tc.rainbow) {
          setSkinProperty(selectedTankId, 'titleRainbow', true);
          removeSkinProp(selectedTankId, 'titleColor');
        } else {
          removeSkinProp(selectedTankId, 'titleRainbow');
          setSkinProperty(selectedTankId, 'titleColor', tc.color);
        }
      }
    } else if (state.skinsShopSection === 6) {
      resetSkin(selectedTankId);
    }
  }

  destroy(): void {
    // Children destroyed with container by parent
  }

  // ─── Helpers ───────────────────────────────────────────

  private drawOptionCell(
    gfx: Graphics, isActive: boolean, isHighlighted: boolean,
    activeColor: number, elapsed: number,
  ): void {
    gfx.clear();
    gfx.roundRect(0, 0, LAYOUT.CELL_SIZE, LAYOUT.CELL_SIZE, LAYOUT.CORNER_RADIUS);
    gfx.fill({ color: 0x0a0a15, alpha: isHighlighted ? 0.7 : 0.3 });
    gfx.roundRect(0, 0, LAYOUT.CELL_SIZE, LAYOUT.CELL_SIZE, LAYOUT.CORNER_RADIUS);

    if (isActive) {
      gfx.stroke({ color: activeColor, width: 2, alpha: 0.9 });
    } else if (isHighlighted) {
      const pulse = 0.6 + Math.sin(elapsed * LAYOUT.GLOW_PULSE_SPEED) * 0.4;
      gfx.stroke({ color: 0xffffff, width: 1.5, alpha: pulse });
    } else {
      gfx.stroke({ color: 0x333333, width: 1, alpha: 0.3 });
    }
  }

  private drawPreviewBorder(borderId: string, bodyId: string, radius: number, elapsed: number): void {
    const gfx = this.previewBorderGfx;
    gfx.clear();
    const t = elapsed;

    if (borderId === 'rainbow') {
      const def = getTankShapeDef(bodyId);
      if (def.type === 'polygon' && def.vertices) {
        const verts = def.vertices(radius);
        const vertCount = verts.length / 2;
        const hueOffset = t * 120;
        for (let i = 0; i < vertCount; i++) {
          const x1 = verts[i * 2], y1 = verts[i * 2 + 1];
          const ni = (i + 1) % vertCount;
          const x2 = verts[ni * 2], y2 = verts[ni * 2 + 1];
          const hue = ((i / vertCount) * 360 + hueOffset) % 360;
          gfx.moveTo(x1, y1); gfx.lineTo(x2, y2);
          gfx.stroke({ color: hueToHex(hue), width: 5, alpha: 0.2 });
        }
        for (let i = 0; i < vertCount; i++) {
          const x1 = verts[i * 2], y1 = verts[i * 2 + 1];
          const ni = (i + 1) % vertCount;
          const x2 = verts[ni * 2], y2 = verts[ni * 2 + 1];
          const hue = ((i / vertCount) * 360 + hueOffset) % 360;
          gfx.moveTo(x1, y1); gfx.lineTo(x2, y2);
          gfx.stroke({ color: hueToHex(hue, 1, 0.6), width: 2.5, alpha: 0.7 });
        }
      } else {
        for (let layer = 0; layer < 3; layer++) {
          const hue = (t * 120 + layer * 120) % 360;
          strokeTankShape(gfx, bodyId, radius + layer * 0.5, hueToHex(hue), 2.5, 0.4);
        }
      }
    } else {
      const style = BORDER_STYLES.find(b => b.id === borderId);
      if (style) {
        const pulse = 0.4 + Math.sin(t * 3) * 0.2;
        strokeTankShape(gfx, bodyId, radius, style.color, 2.5, pulse + 0.3);
      }
    }
  }

  private updateHoverLabel(
    state: GameState, _bodyId: string, _nubId: string,
    _color: number, _borderId: string,
  ): void {
    // Reset hover label color (section 2 may set it to gold)
    (this.hoverLabel.style as TextStyle).fill = 0x999999;

    if (state.skinsShopSection === 1) {
      const nId = NUB_TYPE_IDS[state.skinsShopItem];
      this.hoverLabel.text = NUB_LABELS[nId] ?? nId ?? '';
    } else if (state.skinsShopSection === 2) {
      const c = SKIN_COLORS[state.skinsShopItem];
      if (c) {
        const defColors = getAllTankIds().map(id => lookupTank(id).visual.bodyColor);
        const locked = !isColorUnlocked(c.color, defColors);
        this.hoverLabel.text = locked ? `${c.label} (${COLOR_PRICE})` : c.label;
        (this.hoverLabel.style as TextStyle).fill = locked ? 0xffd700 : 0x999999;
      } else {
        this.hoverLabel.text = '';
      }
    } else if (state.skinsShopSection === 3) {
      const b = BORDER_STYLES[state.skinsShopItem];
      this.hoverLabel.text = b?.label ?? '';
    } else if (state.skinsShopSection === 0) {
      const sId = BODY_SHAPE_IDS[state.skinsShopItem];
      const td = sId ? lookupTank(sId) : null;
      this.hoverLabel.text = td?.name ?? '';
    } else if (state.skinsShopSection === 5) {
      const tc = TITLE_COLORS[state.skinsShopItem];
      this.hoverLabel.text = tc?.label ?? '';
    } else {
      this.hoverLabel.text = '';
    }
  }
}

// ─── Utilities ────────────────────────────────────────────────────────────────

function removeSkinProp(tankId: string, prop: string): void {
  try {
    const store = JSON.parse(localStorage.getItem('geometry-blast-skins') || '{}');
    if (store[tankId]) {
      delete store[tankId][prop];
      if (Object.keys(store[tankId]).length === 0) delete store[tankId];
      localStorage.setItem('geometry-blast-skins', JSON.stringify(store));
    }
  } catch { /* ignore */ }
}

function hueToHex(h: number, s = 1, l = 0.55): number {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return (Math.round(f(0) * 255) << 16) | (Math.round(f(8) * 255) << 8) | Math.round(f(4) * 255);
}
